import {
  createDeliverableInputSchema,
  type CreateDeliverableInput,
  type DeliverableFieldType,
  type DeliverableKind,
  type StaffDeliverable,
  type UpdateDeliverableInput,
} from '@mybusinessmate/schema';

/** One field of the "Add to vault" drawer (Figma A05.11b): a name, a value, and who may see it. */
export interface FormField {
  /** Local key for React; the stored field's id when editing. */
  key: string;
  /** Set when the field already exists: leaving a secret's value empty then keeps what is stored. */
  id?: string;
  label: string;
  type: DeliverableFieldType;
  value: string;
  /** Encrypt and hide ("Secret · encrypt and hide"). A password is always secret. */
  secret: boolean;
  /** A secret that already has a stored value (shown as dots until the team types a new one). */
  stored: boolean;
  visibleToClient: boolean;
  file?: { key: string; name: string; contentType: string; size: number };
}

export interface VaultForm {
  kind: DeliverableKind;
  title: string;
  /** The project it is for, or '' for the whole business ("Link to"). */
  projectId: string;
  description: string;
  fields: FormField[];
}

export type VaultFormErrors = Record<string, string>;

let counter = 0;
const nextKey = () => `f${++counter}`;

export const newField = (over: Partial<FormField> = {}): FormField => ({
  key: nextKey(),
  label: '',
  type: 'text',
  value: '',
  secret: false,
  stored: false,
  visibleToClient: true,
  ...over,
});

/** What a fresh item starts with: a login asks for the three things nearly every login has (Figma A05.11b). */
export function emptyForm(kind: DeliverableKind): VaultForm {
  const fields =
    kind === 'login'
      ? [
          newField({ label: 'URL', type: 'link' }),
          newField({ label: 'Username' }),
          newField({ label: 'Password', type: 'password', secret: true }),
        ]
      : kind === 'file'
        ? [newField({ label: 'File', type: 'file' })]
        : [newField({ label: 'Link', type: 'link' })];
  return { kind, title: '', projectId: '', description: '', fields };
}

/** An item opened for editing: secrets stay empty (they are never sent to the browser) and are marked as stored. */
export function formFromItem(item: StaffDeliverable): VaultForm {
  return {
    kind: item.kind,
    title: item.title,
    projectId: item.projectId ?? '',
    description: item.description ?? '',
    fields: item.fields.map((f) =>
      newField({
        key: f.id,
        id: f.id,
        label: f.label,
        type: f.type,
        value: f.secret || f.type === 'file' ? '' : (f.value ?? ''),
        secret: f.secret,
        stored: f.secret && f.hasValue,
        visibleToClient: f.visibleToClient,
        file: f.file ? { key: '', name: f.file.name, contentType: f.file.contentType, size: f.file.size } : undefined,
      }),
    ),
  };
}

/** "paradisebiryani.in/wp-admin" is how people write a link; the API wants it complete. */
const withScheme = (value: string) => (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);

/** The request for the form as it stands. An edit leaves out a secret that was not re-typed, so it is kept. */
export function toInput(form: VaultForm): CreateDeliverableInput & UpdateDeliverableInput {
  return {
    kind: form.kind,
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    projectId: form.projectId || null,
    fields: form.fields.map((f) => {
      const value = f.value.trim();
      const keep = f.stored && value === '';
      return {
        ...(f.id ? { id: f.id } : {}),
        label: f.label.trim(),
        type: f.type,
        ...(f.type === 'file' || keep ? {} : { value: f.type === 'link' && value ? withScheme(value) : value }),
        // A file still in storage (editing, not replaced) is sent by its id alone.
        ...(f.type === 'file' && f.file?.key ? { file: f.file } : {}),
        secret: f.secret || f.type === 'password',
        visibleToClient: f.visibleToClient,
      };
    }),
  };
}

/**
 * What is wrong with the form, by place: `title`, or `fields.<index>.value` / `.label` / `.file`. The same schema the
 * API checks with, so the drawer never promises something the server will refuse.
 */
export function validateForm(form: VaultForm): VaultFormErrors {
  const parsed = createDeliverableInputSchema.safeParse(toInput(form));
  if (parsed.success) return {};
  const errors: VaultFormErrors = {};
  for (const issue of parsed.error.issues) {
    const path = issue.path.join('.');
    errors[path] ??= issue.message;
  }
  return errors;
}
